import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  HiCheckCircle,
  HiXCircle,
  HiChevronDown,
  HiChevronRight,
  HiChartBar,
  HiArrowDownTray,
  HiDocumentArrowDown,
} from 'react-icons/hi2';
import { useAuth } from '../../context/AuthContext';
import { useUserRole } from '../../context/UserRoleContext';
import {
  getReadinessChecklist,
  saveReadinessChecklist,
  deleteReadinessChecklist,
  canEdit,
  canDelete,
  canApprove,
  updateReadinessStatus,
} from '../../services/commercialisationService';
import { firestore } from '../../database/firebase';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import CommentSection from './CommentSection';
import ConfirmationModal from '../SharedFeature/ConfirmationModal';

const CATEGORIES = [
  {
    id: 'productMarketFit',
    title: 'Product & Market Fit',
    items: [
      'Product solves a clear problem for target customers',
      'Customer validation completed (surveys, interviews)',
      'Product-Market Fit indicators are positive',
      'Market demand confirmed through research',
      'Competitive advantage identified and validated',
      'Target customer segments clearly defined',
      'Customer feedback incorporated into product',
      'MVP or prototype tested with real users',
    ],
  },
  {
    id: 'businessModelRevenue',
    title: 'Business Model & Revenue',
    items: [
      'Revenue model clearly defined (subscription, one-time, etc.)',
      'Pricing strategy tested and validated',
      'Unit economics are positive',
      'Revenue projections for 12-24 months completed',
      'Break-even analysis completed',
      'Customer acquisition cost (CAC) calculated',
      'Lifetime value (LTV) of customer calculated',
      'LTV/CAC ratio is favorable (3:1 or better)',
    ],
  },
  {
    id: 'gtmStrategy',
    title: 'Go-To-Market (GTM) Strategy',
    items: [
      'GTM strategy documented and approved',
      'Marketing channels identified and prioritized',
      'Sales strategy and process defined',
      'Distribution channels selected',
      'Partnership strategy in place',
      'Launch timeline and milestones defined',
      'Marketing budget allocated',
      'Brand positioning and messaging clear',
    ],
  },
  {
    id: 'productTechInfrastructure',
    title: 'Product & Tech Infrastructure',
    items: [
      'Product is production-ready and scalable',
      'Technical infrastructure supports growth',
      'Security and compliance measures in place',
      'Data backup and recovery systems operational',
      'Quality assurance processes established',
      'Support systems and tools in place',
      'Analytics and monitoring systems configured',
      'Documentation and knowledge base created',
    ],
  },
  {
    id: 'legalCompliance',
    title: 'Legal & Compliance',
    items: [
      'Business registration and licenses obtained',
      'Intellectual property protected (trademarks, patents)',
      'Terms of Service and Privacy Policy published',
      'Data protection and GDPR compliance ensured',
      'Contracts with suppliers/partners executed',
      'Employment agreements and policies in place',
      'Insurance coverage obtained',
      'Regulatory compliance verified',
    ],
  },
  {
    id: 'operationsDelivery',
    title: 'Operations & Delivery Capacity',
    items: [
      'Operations processes documented',
      'Supply chain or service delivery established',
      'Inventory management systems in place (if applicable)',
      'Customer service processes defined',
      'Quality control measures implemented',
      'Scalability of operations validated',
      'Vendor and supplier relationships established',
      'Delivery capacity matches demand projections',
    ],
  },
  {
    id: 'teamGovernance',
    title: 'Team & Governance',
    items: [
      'Core team roles filled and defined',
      'Organizational structure established',
      'Governance structure in place',
      'Key performance indicators (KPIs) defined',
      'Communication and collaboration tools in use',
      'Performance management processes established',
      'Training and onboarding processes ready',
      'Succession planning considered',
    ],
  },
  {
    id: 'fundingInvestment',
    title: 'Funding & Investment Readiness',
    items: [
      'Funding requirements calculated',
      'Funding sources identified and secured',
      'Financial projections completed and reviewed',
      'Investor pitch deck prepared (if applicable)',
      'Due diligence materials organized',
      'Financial controls and reporting systems in place',
      'Cash flow management plan established',
      'Investment terms and agreements finalized',
    ],
  },
];

const CommercialisationReadinessChecklist = ({ userRole: propUserRole, userId: propUserId }) => {
  const { currentUser } = useAuth();
  const { userRole: contextUserRole } = useUserRole();
  const effectiveUserRole = propUserRole || contextUserRole || 'client';
  const effectiveUserId = propUserId || currentUser?.uid;
  const [formData, setFormData] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [approvalComments, setApprovalComments] = useState('');
  const [showApprovalForm, setShowApprovalForm] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved'
  const [isCommercializedClient, setIsCommercializedClient] = useState(false);
  const [clientBusinessStage, setClientBusinessStage] = useState('');
  const [hasLaunched, setHasLaunched] = useState(false);
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false });
  const [isDeleting, setIsDeleting] = useState(false);

  // Calculate scores
  const scores = useMemo(() => {
    const categoryScores = {};
    let totalChecked = 0;
    let totalItems = 0;

    CATEGORIES.forEach(category => {
      const checked = category.items.filter(
        (_, index) => formData[`${category.id}_${index}`]
      ).length;
      const total = category.items.length;
      const percentage = total > 0 ? Math.round((checked / total) * 100) : 0;
      categoryScores[category.id] = { checked, total, percentage };
      totalChecked += checked;
      totalItems += total;
    });

    const overallScore = totalItems > 0 ? Math.round((totalChecked / totalItems) * 100) : 0;

    let readinessLevel = 'Not Ready';
    let readinessColor = 'red';
    if (overallScore >= 85) {
      readinessLevel = 'Ready to Commercialise';
      readinessColor = 'green';
    } else if (overallScore >= 60) {
      readinessLevel = 'Almost Ready';
      readinessColor = 'yellow';
    }

    return {
      categoryScores,
      overallScore,
      readinessLevel,
      readinessColor,
      totalChecked,
      totalItems,
    };
  }, [formData]);

  // Real-time listener for Readiness Checklist data
  useEffect(() => {
    if (!effectiveUserId || !currentUser?.uid) {
      setLoading(false);
      return;
    }

    const docRef = doc(
      firestore,
      'commercialisation',
      effectiveUserId,
      'readinessChecklist',
      effectiveUserId
    );

    const unsubscribe = onSnapshot(
      docRef,
      docSnap => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setFormData(data.responses || {});
          setApprovalStatus(data.status || null);
          setApprovalComments(data.comments || '');
        }
        setLoading(false);
      },
      error => {
        toast.error('Failed to load checklist');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [effectiveUserId, currentUser?.uid]);

  useEffect(() => {
    const fetchClientStage = async () => {
      if (!effectiveUserId) return;
      try {
        const [clientSnap, profileSnap] = await Promise.all([
          getDoc(doc(firestore, 'clients', effectiveUserId)),
          getDoc(doc(firestore, 'companyProfiles', effectiveUserId)),
        ]);

        if (clientSnap.exists()) {
          const stage = clientSnap.data()?.businessStage || '';
          setClientBusinessStage(stage);
          const lowerStage = stage.toString().toLowerCase();
          const isCommercial = [
            'commercialized',
            'commercialised',
            'commercialisation',
            'commercialization',
          ].some(flag => lowerStage.includes(flag));
          setIsCommercializedClient(isCommercial);
        } else {
          setClientBusinessStage('');
          setIsCommercializedClient(false);
        }

        if (profileSnap.exists()) {
          setHasLaunched(Boolean(profileSnap.data()?.isLaunched));
        } else {
          setHasLaunched(false);
        }
      } catch (error) {
        setClientBusinessStage('');
        setIsCommercializedClient(false);
        setHasLaunched(false);
      }
    };

    fetchClientStage();
  }, [effectiveUserId]);

  // Auto-save with visual feedback (only for clients editing their own)
  const isClientActor = (effectiveUserRole || '').toLowerCase() === 'client';
  const isReadOnly = isClientActor && hasLaunched;

  useEffect(() => {
    if (!effectiveUserId || !currentUser?.uid || loading) return;
    if (
      effectiveUserId !== currentUser.uid ||
      !canEdit(effectiveUserRole, currentUser.uid, effectiveUserId) ||
      isReadOnly
    )
      return;
    if (!hasUserEdited) return; // only autosave after a user-initiated edit

    const autoSaveTimer = setTimeout(async () => {
      try {
        setAutoSaveStatus('saving');
        await saveReadinessChecklist(
          effectiveUserId,
          {
            responses: formData,
            score: scores.overallScore,
            readinessLevel: scores.readinessLevel,
            categoryScores: scores.categoryScores,
            totalChecked: scores.totalChecked,
            totalItems: scores.totalItems,
          },
          currentUser
        );
        setAutoSaveStatus('saved');
        setHasUserEdited(false);
        setTimeout(() => setAutoSaveStatus('idle'), 1500);
      } catch (error) {
        setAutoSaveStatus('idle');
      }
    }, 1200);

    return () => clearTimeout(autoSaveTimer);
  }, [
    formData,
    effectiveUserId,
    currentUser?.uid,
    loading,
    scores,
    effectiveUserRole,
    hasUserEdited,
    isReadOnly,
  ]);

  const toggleCategory = categoryId => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleCheckboxChange = (categoryId, index) => {
    if (isReadOnly) return;
    const key = `${categoryId}_${index}`;
    setFormData(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
    setHasUserEdited(true);
  };

  const handleSave = async () => {
    if (!effectiveUserId || !currentUser?.uid) {
      toast.error('Please sign in to save');
      return;
    }

    if (!canEdit(effectiveUserRole, currentUser.uid, effectiveUserId) || isReadOnly) {
      toast.error('Access denied');
      return;
    }

    setSaving(true);
    try {
      await saveReadinessChecklist(
        effectiveUserId,
        {
          responses: formData,
          score: scores.overallScore,
          readinessLevel: scores.readinessLevel,
          categoryScores: scores.categoryScores,
          totalChecked: scores.totalChecked,
          totalItems: scores.totalItems,
        },
        currentUser
      );

      toast.success('Progress saved successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    if (!effectiveUserId || !currentUser?.uid) {
      toast.error('Please sign in to delete');
      return;
    }

    if (!canDelete(effectiveUserRole, currentUser.uid, effectiveUserId)) {
      toast.error('Access denied');
      return;
    }

    setDeleteConfirm({ isOpen: true });
  };

  const handleDeleteConfirm = async () => {
    if (!effectiveUserId || !currentUser?.uid) return;

    setIsDeleting(true);
    try {
      await deleteReadinessChecklist(effectiveUserId, currentUser);
      setFormData({});
      setDeleteConfirm({ isOpen: false });
      toast.success('Checklist deleted successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to delete checklist');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false });
  };

  const handleApprove = async status => {
    if (!effectiveUserId || !currentUser?.uid) {
      toast.error('Please sign in');
      return;
    }

    if (!canApprove(effectiveUserRole)) {
      toast.error('Access denied');
      return;
    }

    setSaving(true);
    try {
      await updateReadinessStatus(effectiveUserId, status, approvalComments, currentUser);
      setApprovalStatus(status);
      setShowApprovalForm(false);
      toast.success(`Status updated to ${status}`);
    } catch (error) {
      toast.error(error.message || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    const data = {
      timestamp: new Date().toISOString(),
      score: scores.overallScore,
      readinessLevel: scores.readinessLevel,
      categoryScores: scores.categoryScores,
      responses: formData,
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commercialisation-readiness-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success('Data exported successfully!');
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
          <p className="text-sm text-gray-500">Loading checklist...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 md:py-8 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="mb-2 text-3xl font-bold text-black">
                Commercialisation Readiness Checklist
              </h1>
              <p className="text-gray-600">
                Assess your readiness for commercialisation with this comprehensive checklist
              </p>
              {isClientActor && (isCommercializedClient || hasLaunched) && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                  <span className="block">
                    Commercialisation progress and launch status are managed separately. A client
                    can be commercialised without having launched.
                  </span>
                  {hasLaunched ? (
                    <span className="mt-2 block text-xs text-amber-600/90">
                      The company profile launch status is set to <strong>Launched</strong>, so this
                      checklist is read-only. Update the launch toggle in the company profile if the
                      status changes.
                    </span>
                  ) : (
                    <span className="mt-2 block text-xs text-amber-600/80">
                      Current business stage: {clientBusinessStage || 'Commercialised'}. Because the
                      launch toggle is still off, you retain edit access until you officially mark
                      the launch on your company profile.
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              {/* Auto-save indicator */}
              {canEdit(effectiveUserRole, currentUser?.uid, effectiveUserId) && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center gap-2 text-sm text-gray-500"
                >
                  {autoSaveStatus === 'saving' && (
                    <>
                      <div className="h-2 w-2 animate-pulse rounded-full bg-blue-500" />
                      <span>Saving...</span>
                    </>
                  )}
                  {autoSaveStatus === 'saved' && (
                    <>
                      <HiCheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-green-600">Saved</span>
                    </>
                  )}
                </motion.div>
              )}
              {canEdit(effectiveUserRole, currentUser?.uid, effectiveUserId) && (
                <button
                  onClick={handleSave}
                  disabled={saving || isReadOnly}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50"
                >
                  <HiDocumentArrowDown className="h-4 w-4" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
              )}
              {canDelete(effectiveUserRole, currentUser?.uid, effectiveUserId) && (
                <button
                  onClick={handleDeleteClick}
                  disabled={saving || isDeleting}
                  className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 shadow-sm transition-colors hover:bg-red-50 disabled:opacity-50"
                >
                  <HiXCircle className="h-4 w-4" />
                  Delete
                </button>
              )}
              <button
                onClick={handleExport}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
              >
                <HiArrowDownTray className="h-4 w-4" />
                Export
              </button>
              {canApprove(effectiveUserRole) && (
                <button
                  onClick={() => setShowApprovalForm(!showApprovalForm)}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
                >
                  <HiCheckCircle className="h-4 w-4" />
                  {approvalStatus ? `Status: ${approvalStatus}` : 'Approve/Review'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Overall Score Card */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Overall Readiness Score</h2>
              <p className="mt-1 text-sm text-gray-600">
                {scores.totalChecked} of {scores.totalItems} items completed
              </p>
            </div>
            <div className="text-right">
              <div
                className={`text-4xl font-bold ${
                  scores.readinessColor === 'green'
                    ? 'text-green-600'
                    : scores.readinessColor === 'yellow'
                      ? 'text-yellow-600'
                      : 'text-red-600'
                }`}
              >
                {scores.overallScore}%
              </div>
              <div
                className={`mt-1 text-sm font-medium ${
                  scores.readinessColor === 'green'
                    ? 'text-green-700'
                    : scores.readinessColor === 'yellow'
                      ? 'text-yellow-700'
                      : 'text-red-700'
                }`}
              >
                {scores.readinessLevel}
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${scores.overallScore}%` }}
                transition={{ duration: 0.5 }}
                className={`h-full ${
                  scores.readinessColor === 'green'
                    ? 'bg-green-600'
                    : scores.readinessColor === 'yellow'
                      ? 'bg-yellow-600'
                      : 'bg-red-600'
                }`}
              />
            </div>
          </div>
        </motion.div>

        {/* Categories */}
        <div className="space-y-4">
          {CATEGORIES.map((category, categoryIndex) => {
            const isExpanded = expandedCategories[category.id];
            const categoryScore = scores.categoryScores[category.id];
            const progress = categoryScore?.percentage || 0;

            return (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: categoryIndex * 0.1 }}
                className="rounded-xl border border-gray-200 bg-white shadow-sm"
              >
                <button
                  onClick={() => toggleCategory(category.id)}
                  className="flex w-full items-center justify-between p-6 text-left hover:bg-gray-50"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-50">
                      <HiChartBar className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{category.title}</h3>
                      <p className="mt-1 text-sm text-gray-600">
                        {categoryScore?.checked || 0} of {categoryScore?.total || 0} items completed
                        ({progress}%)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900">{progress}%</div>
                    </div>
                    {isExpanded ? (
                      <HiChevronDown className="h-5 w-5 text-gray-400" />
                    ) : (
                      <HiChevronRight className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Progress Bar */}
                <div className="px-6 pb-4">
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <div
                      className={`h-full transition-all duration-300 ${
                        progress >= 85
                          ? 'bg-green-600'
                          : progress >= 60
                            ? 'bg-yellow-600'
                            : 'bg-red-600'
                      }`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-gray-200 px-6 py-4"
                    >
                      <div className="space-y-3">
                        {category.items.map((item, index) => {
                          const key = `${category.id}_${index}`;
                          const isChecked = formData[key] || false;

                          return (
                            <label
                              key={key}
                              className={`flex items-start gap-3 rounded-lg p-3 transition-colors ${
                                isReadOnly
                                  ? 'cursor-not-allowed opacity-70'
                                  : 'cursor-pointer hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleCheckboxChange(category.id, index)}
                                className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                disabled={isReadOnly}
                              />
                              <span
                                className={`flex-1 text-sm ${
                                  isChecked ? 'font-medium text-gray-900' : 'text-gray-600'
                                }`}
                              >
                                {item}
                              </span>
                              {isChecked && (
                                <HiCheckCircle className="h-5 w-5 flex-shrink-0 text-green-600" />
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Approval Form */}
        {showApprovalForm && canApprove(effectiveUserRole) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 rounded-xl border border-blue-200 bg-blue-50 p-6"
          >
            <h3 className="mb-4 text-lg font-semibold text-gray-900">Approve/Review Checklist</h3>
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={approvalStatus || ''}
                  onChange={e => setApprovalStatus(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                >
                  <option value="">Select status...</option>
                  <option value="Approved">Approved</option>
                  <option value="Needs Review">Needs Review</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Comments</label>
                <textarea
                  value={approvalComments}
                  onChange={e => setApprovalComments(e.target.value)}
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                  placeholder="Add your comments or feedback..."
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => handleApprove(approvalStatus)}
                  disabled={!approvalStatus || saving}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Update Status'}
                </button>
                <button
                  onClick={() => setShowApprovalForm(false)}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Category Scores Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8 rounded-xl border border-gray-200 bg-gray-50 p-6"
        >
          <h3 className="mb-4 text-lg font-semibold text-gray-900">Category Breakdown</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {CATEGORIES.map(category => {
              const categoryScore = scores.categoryScores[category.id];
              const progress = categoryScore?.percentage || 0;

              return (
                <div key={category.id} className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="mb-2 text-sm font-medium text-gray-700">{category.title}</div>
                  <div className="flex items-center gap-2">
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
                      <div
                        className={`h-full ${
                          progress >= 85
                            ? 'bg-green-600'
                            : progress >= 60
                              ? 'bg-yellow-600'
                              : 'bg-red-600'
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{progress}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Comment Section */}
        {effectiveUserId && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-8"
          >
            <CommentSection userId={effectiveUserId} checklistType="readiness" />
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default CommercialisationReadinessChecklist;
